










































































































































































































































































import { cloneDeep } from 'lodash';
import { mapActions } from 'vuex';
import { AxiosError } from 'axios';

import {
  IModuleAccessoryType,
  IModulePaymentType,
  Module,
  ModuleAccessoryType,
  ModuleAccessoryTypeEnum,
  ModuleFieldError,
  ModulePaymentType
} from '@/models/configurator/module';
import { ErrorManager } from '@/models/error';

import FormRules from '@/mixins/file-upload-rules';
import mixins from '@/utils/mixins';

import ALDialog from '../core/Dialog/ALDialog.vue';
import ErrorAlert from '../common/ErrorAlert.vue';
import DragDropFileInput from '../common/DragDropFileInput.vue';
import ModuleImage from './modules/ModuleImage.vue';

import { ALForm } from '@/common/component.types';

const baseMixins = mixins(FormRules);

export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: ALForm;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'ModuleFormDialog',

    components: { ALDialog, ErrorAlert, DragDropFileInput, ModuleImage },

    data: () => ({
      dragging: false,
      loading: false,
      createForm: true,
      dialog: false,
      alert: false,
      requiredRules: [(v: string) => !!v || 'This field is required'],
      typeRules: [(v: string) => v != null || 'This field is required'],
      show: false,
      error: null as null | string,
      valid: true,
      brands: ['JCMI', 'Jamesway', 'Chick Master', 'Airway'],

      module: new Module(),
      errorDetail: {
        text: [],
        type: [],
        hint: [],
        choices: []
      },
      options: {
        type: [
          {
            name: 'Plain Text',
            value: 0
          },
          {
            name: 'Single Choice',
            value: 1
          },
          {
            name: 'Multiple Choice',
            value: 2
          }
        ]
      },

      choices: [] as any[],
      newModule: null as any,

      itemImage: null as any | string,

      errorMessage: '',
      detailErrorMessage: '',

      fieldError: new ModuleFieldError()
    }),

    computed: {
      computedModuleImage: {
        get(): File | null {
          let computedFile: File | null = this.module.image as File;

          if (typeof computedFile !== 'object') {
            computedFile = null;
          }
          return computedFile;
        },
        set(file: File | undefined) {
          this.module.image = file;
        }
      },

      modulePaymentTypeList(): IModulePaymentType[] {
        return ModulePaymentType.getModulePaymentTypeList();
      },

      moduleAccessoryTypeList(): IModuleAccessoryType[] {
        return ModuleAccessoryType.getModuleAccessoryTypeList();
      }
    },

    methods: {
      ...mapActions('configuratorModule', ['createModule', 'updateModule']),
      async open(module?: Module) {
        if (module) {
          this.createForm = false;
          this.module = cloneDeep(module);
          if (module.image_url) {
            // when editing; load image if there is one to load
            await this.loadImage(module);
          }
        } else {
          this.createForm = true;
          this.module = new Module();
          this.itemImage = null;
        }

        this.alert = false;
        this.clearAllErrors();

        this.dialog = true;
      },

      close() {
        this.resetFormValidation();
        this.module = new Module();
        this.revokeImageURL();
        this.dialog = false;
      },

      resetFormValidation(): void {
        if (this.$refs.form) {
          return this.$refs.form.resetValidation();
        }
      },

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.form.validate();
        }
        return valid;
      },

      onModuleImageChange(file: File) {
        // if we were to update image then revoke object url to avoid mem-leak
        if (file) {
          this.itemImage = URL.createObjectURL(file);
        } else {
          this.itemImage = null;
        }

        this.clearAllErrors();
      },

      onUpdateAccessoryType(isAccessory: boolean) {
        // if creating then set accessory type to configurable
        if (isAccessory && this.createForm) {
          this.module.accessory_type = ModuleAccessoryTypeEnum.Configurable;
        } else {
          this.module.accessory_type = ModuleAccessoryTypeEnum.Legacy;
        }
      },

      /**
       * Once the image is loaded, revoke the object url (avoid memory leak).
       */
      revokeImageURL() {
        if (this.itemImage) URL.revokeObjectURL(this.itemImage);
      },

      loadImage(module: Module) {
        return (
          this.$services.fileLoader
            .downloadFile(module.image_url as string)
            .then(data => {
              this.itemImage = URL.createObjectURL(data);

              return data;
            })
            // Not to display error
            .catch(error => {
              return null;
            })
            .finally(() => {
              this.loading = false;
            })
        );
      },

      clearAllErrors(): void {
        this.fieldError.clearErrors();
        this.errorMessage = '';
        this.detailErrorMessage = '';
      },

      async save() {
        if (this.validateForm()) {
          this.clearAllErrors();
          try {
            if (this.createForm) {
              return await this.createModule(this.module).then(() => {
                this.$emit('submitted');
                this.close();
              });
            } else {
              if (typeof this.module.image === 'string') {
                delete this.module.image;
              }
              return await this.updateModule(this.module).then(() => {
                this.$emit('submitted');
                this.close();
              });
            }
          } catch (err) {
            const error = err as AxiosError;
            if (error.response) {
              // client received an error response that falls out of range 2xx
              if (ModuleFieldError.isModuleFieldError(error.response.data)) {
                this.fieldError = new ModuleFieldError(error.response.data);
              }
              if (error.response.data.detail) {
                this.detailErrorMessage = error.response.data.detail;
              }
            }
            this.errorMessage = ErrorManager.extractApiError(error);
          }
        }
      }
    }
  });
